import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SavingsBarChart = ({ recommendations }) => {
  const { t, i18n } = useTranslation();
  const [viewMode, setViewMode] = useState('cost'); // 'cost' | 'energy'

  const formatCurrency = (amount, currency = 'USD', locale = 'en') => {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
  };

  const chartData = recommendations.map(rec => {
    // 1. Cost Calculations
    const savings = rec.savings_estimation_usd || 0;
    let currentCost = 0;
    let optimizedCost = 0;

    const currConf = rec.current_configuration || {};
    const recomConf = rec.recommended_configuration || {};

    const getCost = (obj) => {
      const keys = ['cost', 'price', 'monthly_cost', 'monthly_price', 'estimated_cost', 'current_cost', 'cost_usd'];
      for (let k of keys) {
        if (obj[k] !== undefined && !isNaN(parseFloat(obj[k]))) {
          return parseFloat(obj[k]);
        }
      }
      return null;
    };

    const extractedCurrent = getCost(currConf);
    const extractedOptimized = getCost(recomConf);

    if (extractedCurrent && extractedCurrent > 0) {
      currentCost = extractedCurrent;
      optimizedCost = extractedOptimized || (currentCost - savings);
    } else {
      optimizedCost = Math.max(20, savings * 0.8);
      currentCost = optimizedCost + savings;
    }

    // 2. Energy Calculations (Exact data contract from backend)
    const currentEnergy = parseFloat(rec.energy_consumption_kwh) || 0.0;
    const energySavings = parseFloat(rec.energy_reduction_potential_kwh) || 0.0;
    const optimizedEnergy = Math.max(0, currentEnergy - energySavings);

    return {
      resourceId: rec.resource_id.length > 12 ? rec.resource_id.substring(0, 12) + '...' : rec.resource_id,
      fullName: rec.resource_id,
      currentCost: parseFloat(currentCost.toFixed(2)),
      optimizedCost: parseFloat(optimizedCost.toFixed(2)),
      savings: parseFloat(savings.toFixed(2)),
      currentEnergy: parseFloat(currentEnergy.toFixed(2)),
      optimizedEnergy: parseFloat(optimizedEnergy.toFixed(2)),
      energySavings: parseFloat(energySavings.toFixed(2))
    };
  });

  const formatTooltipValue = (value) => {
    if (value === undefined || value === null) return '0.00';
    const num = Number(value);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload || {};
      if (viewMode === 'cost') {
        return (
          <div style={{
            backgroundColor: '#0F172A',
            border: '1px solid rgba(79, 70, 229, 0.4)',
            padding: '12px 16px',
            borderRadius: '10px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
            textAlign: 'left'
          }}>
            <p style={{ color: '#FFF', margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '0.85rem' }}>
              {data.fullName}
            </p>
            <p style={{ color: '#94A3B8', margin: '0 0 4px 0', fontSize: '0.8rem' }}>
              {t('finops.chart_current')}: <span style={{ color: '#F87171', fontWeight: '600' }}>{formatCurrency(data.currentCost || 0, 'USD', i18n.language)}</span>
            </p>
            <p style={{ color: '#94A3B8', margin: '0 0 4px 0', fontSize: '0.8rem' }}>
              {t('finops.chart_optimized')}: <span style={{ color: '#34D399', fontWeight: '600' }}>{formatCurrency(data.optimizedCost || 0, 'USD', i18n.language)}</span>
            </p>
            <p style={{ color: '#A5B4FC', margin: '0', fontSize: '0.8rem', borderTop: '1px solid var(--glass-border)', paddingTop: '6px', marginTop: '6px' }}>
              {t('finops.chart_saving_estimation')}: <span style={{ color: '#60A5FA', fontWeight: '700' }}>{formatCurrency(data.savings || 0, 'USD', i18n.language)}</span>
            </p>
          </div>
        );
      } else {
        return (
          <div style={{
            backgroundColor: '#0F172A',
            border: '1px solid rgba(16, 185, 129, 0.4)',
            padding: '12px 16px',
            borderRadius: '10px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
            textAlign: 'left'
          }}>
            <p style={{ color: '#FFF', margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '0.85rem' }}>
              {data.fullName}
            </p>
            <p style={{ color: '#94A3B8', margin: '0 0 4px 0', fontSize: '0.8rem' }}>
              {t('finops.chart_energy_current')}: <span style={{ color: '#60A5FA', fontWeight: '600' }}>{formatTooltipValue(data.currentEnergy)} kWh</span>
            </p>
            <p style={{ color: '#94A3B8', margin: '0 0 4px 0', fontSize: '0.8rem' }}>
              {t('finops.chart_energy_optimized')}: <span style={{ color: '#34D399', fontWeight: '600' }}>{formatTooltipValue(data.optimizedEnergy)} kWh</span>
            </p>
            <p style={{ color: '#A7F3D0', margin: '0', fontSize: '0.8rem', borderTop: '1px solid var(--glass-border)', paddingTop: '6px', marginTop: '6px' }}>
              {t('finops.energy_reduction_potential')}: <span style={{ color: '#10B981', fontWeight: '700' }}>{formatTooltipValue(data.energySavings)} kWh</span>
            </p>
          </div>
        );
      }
    }
    return null;
  };

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.65)',
    border: '1px solid rgba(79, 70, 229, 0.28)',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '30px',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
    textAlign: 'center',
    minWidth: 0
  };

  return (
    <div style={cardStyle} className="min-w-0">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
        <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: '#E2E8F0' }}>
          {viewMode === 'cost' ? t('finops.chart_title') : t('finops.chart_title_energy')}
        </h3>
        
        {/* Toggle Switch stylized with Tailwind CSS */}
        <div className="flex bg-slate-800/80 rounded-xl p-1 border border-slate-700/50">
          <button
            onClick={() => setViewMode('cost')}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold border-none cursor-pointer transition-all duration-200 ${
              viewMode === 'cost'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-transparent text-slate-400 hover:text-white'
            }`}
          >
            {t('finops.chart_view_cost')}
          </button>
          <button
            onClick={() => setViewMode('energy')}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold border-none cursor-pointer transition-all duration-200 ${
              viewMode === 'energy'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-transparent text-slate-400 hover:text-white'
            }`}
          >
            {t('finops.chart_view_energy')}
          </button>
        </div>
      </div>

      <div style={{ height: '320px', width: '100%', minWidth: 0, minHeight: '250px', position: 'relative' }} className="min-w-0">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={250}>
          <BarChart
            key={viewMode}
            data={chartData}
            margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
            barGap={4}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis
              dataKey="resourceId"
              stroke="#64748B"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            <YAxis
              type="number"
              domain={[0, 'auto']}
              allowDecimals={true}
              stroke="#64748B"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickFormatter={(v) => viewMode === 'cost' ? `$${v}` : `${v} kWh`}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.02)' }} />
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
              formatter={(value) => {
                if (viewMode === 'cost') {
                  if (value === 'currentCost') return <span style={{ color: '#E2E8F0' }}>{t('finops.chart_current')}</span>;
                  if (value === 'optimizedCost') return <span style={{ color: '#E2E8F0' }}>{t('finops.chart_optimized')}</span>;
                } else {
                  if (value === 'currentEnergy') return <span style={{ color: '#E2E8F0' }}>{t('finops.chart_energy_current')}</span>;
                  if (value === 'optimizedEnergy') return <span style={{ color: '#E2E8F0' }}>{t('finops.chart_energy_optimized')}</span>;
                }
                return value;
              }}
            />
            <Bar
              dataKey={viewMode === 'cost' ? "currentCost" : "currentEnergy"}
              fill={viewMode === 'cost' ? "url(#currentGrad)" : "url(#currentEnergyGrad)"}
              radius={[4, 4, 0, 0]}
              isAnimationActive={true}
              animationDuration={800}
            />
            <Bar
              dataKey={viewMode === 'cost' ? "optimizedCost" : "optimizedEnergy"}
              fill={viewMode === 'cost' ? "url(#optimizedGrad)" : "url(#optimizedEnergyGrad)"}
              radius={[4, 4, 0, 0]}
              isAnimationActive={true}
              animationDuration={800}
            />
            
            {/* Gradients */}
            <defs>
              <linearGradient id="currentGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#EF4444" stopOpacity={0.85} />
                <stop offset="100%" stopColor="#991B1B" stopOpacity={0.85} />
              </linearGradient>
              <linearGradient id="optimizedGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity={0.85} />
                <stop offset="100%" stopColor="#065F46" stopOpacity={0.85} />
              </linearGradient>
              <linearGradient id="currentEnergyGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.85} />
                <stop offset="100%" stopColor="#1D4ED8" stopOpacity={0.85} />
              </linearGradient>
              <linearGradient id="optimizedEnergyGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity={0.85} />
                <stop offset="100%" stopColor="#047857" stopOpacity={0.85} />
              </linearGradient>
            </defs>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SavingsBarChart;
